import { FunctionComponent } from "react";

import { faTrash, faCheck, faXmark, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Toggle } from "@/components/Toggle/Toggle";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { InputField } from "@/components/InputField/InputField";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import {
	FieldMapping,
	FieldMappingAiConfig,
	FieldMappingSourceType,
	HubspotEntityType,
	HubspotField,
	SourceField,
} from "../schema/hubspot";
import {
	AiHintBanner,
	AiSourceCell,
	CannotMapRow,
	SourceTypePills,
	TargetFieldBadges,
} from "./fieldMapping";
import { findConstantOptionMismatch } from "./hubspotFieldMapping.utils";

type Ts = (key: string, options?: Record<string, unknown>) => string;

const buildBadgeLabels = (ts: Ts) => ({
	required: ts("metaBadge.required"),
	multi: ts("metaBadge.multi"),
	dropdown: ts("metaBadge.dropdown"),
	number: ts("metaBadge.number"),
	calculated: ts("metaBadge.calculated"),
	readOnly: ts("metaBadge.readOnly"),
	createOnly: ts("metaBadge.createOnly"),
	createOnlyTooltip: ts("metaBadge.createOnlyTooltip"),
});

/**
 * Create-only fields (HubSpot's "Record source detail 2/3") stay selectable: HubSpot reports them
 * as read-only but accepts them on create, and the API strips them from update payloads. The API
 * already accounts for that in `isReadOnly`, so this only has to keep them out of the unwritable
 * bucket if a stale payload still carries the old flag.
 */
const isFieldUnwritable = (field: HubspotField | undefined): boolean =>
	!field?.isCreateOnly && (Boolean(field?.isReadOnly) || Boolean(field?.isCalculated));

interface MappingRowProps {
	mapping: FieldMapping;
	sourceFields: SourceField[];
	hubspotFields: HubspotField[];
	onToggleActive: (id: number, isActive: boolean) => void;
	onDelete: (id: number) => void;
	onConfigureAi: (mapping: FieldMapping) => void;
	ts: Ts;
}

export const HubspotMappingRow: FunctionComponent<MappingRowProps> = ({
	mapping,
	sourceFields,
	hubspotFields,
	onToggleActive,
	onDelete,
	onConfigureAi,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const hubspotField = hubspotFields.find((f) => f.name === mapping.hubspotField);
	const hubspotLabel = hubspotField
		? `${hubspotField.label}${hubspotField.isCustom ? " (Custom)" : ""}`
		: mapping.hubspotField;
	const badgeLabels = buildBadgeLabels(ts);

	if (isFieldUnwritable(hubspotField)) {
		return (
			<CannotMapRow
				badgeLabels={badgeLabels}
				cannotMapLabel={ts("cannotMap")}
				targetField={hubspotField || {}}
				targetFieldLabel={hubspotLabel}
				testIdPrefix="hubspot"
				tooltip={ts("cannotMapTooltip")}
				onDelete={() => onDelete(mapping.id)}
			/>
		);
	}

	const sourceLabel = isConstant
		? ts("constantLabel", { value: mapping.sourceField })
		: sourceFields.find((f) => f.name === mapping.sourceField)?.label || mapping.sourceField;
	// PREC-3967: a literal no option of the target accepts fails every synced record with 400.
	const constantMismatch = isConstant
		? findConstantOptionMismatch(mapping.sourceField, hubspotField)
		: null;

	return (
		<div className="flex items-center gap-3 py-3 border-b border-gray-100">
			<div className="flex-1">
				{isAi ? (
					<AiSourceCell
						badgeLabel={ts("aiBadge")}
						configureLabel={ts("aiConfigure")}
						creditTooltip={ts("aiCreditTooltip")}
						label={hubspotLabel}
						testIdPrefix="hubspot"
						onConfigure={() => onConfigureAi(mapping)}
					/>
				) : (
					<div
						className={classNames(
							"py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-gray-50 text-text",
							isConstant && "italic text-text-meta"
						)}
						data-testid={isConstant ? "hubspot-constant-label" : "hubspot-field-label"}
					>
						{sourceLabel}
					</div>
				)}
				{constantMismatch && (
					<Tooltip
						content={ts("constantNotAnOptionTooltip", {
							options: constantMismatch.options.join(", "),
						})}
					>
						<span
							className="mt-1 inline-block text-xs text-status-error"
							data-testid="hubspot-constant-invalid"
						>
							{ts("constantNotAnOption")}
						</span>
					</Tooltip>
				)}
			</div>
			<div className="w-[24px] flex items-center justify-center">
				<Icon className="w-[16px] h-[16px] text-gray-400" icon={faArrowRight} />
			</div>
			<div className="flex-1">
				<div className="py-[9px] px-3 rounded-xl text-ssm border border-border-light bg-gray-50 text-text flex items-center gap-2">
					<span className="flex-1 truncate">{hubspotLabel}</span>
					<TargetFieldBadges
						field={hubspotField || {}}
						labels={badgeLabels}
						testIdPrefix="hubspot"
					/>
				</div>
			</div>
			<div className="w-[60px] flex items-center justify-center">
				<Toggle
					isChecked={mapping.isActive}
					onChange={() => onToggleActive(mapping.id, !mapping.isActive)}
				/>
			</div>
			<div
				className="w-fit bg-status-error-light flex justify-end p-1 rounded border-border-light group hover:border-text-status-error cursor-pointer"
				onClick={() => onDelete(mapping.id)}
			>
				<Icon className="text-status-error cursor-pointer w-4 h-4" icon={faTrash} />
			</div>
		</div>
	);
};

export interface NewHubspotMapping {
	tempId: string;
	sourceField: string;
	sourceType: FieldMappingSourceType;
	aiConfig?: FieldMappingAiConfig;
	hubspotField: string;
	isActive: boolean;
	entityType: HubspotEntityType;
}

interface NewMappingRowProps {
	mapping: NewHubspotMapping;
	sourceFields: SourceField[];
	hubspotFields: HubspotField[];
	usedSourceFields: string[];
	usedHubspotFieldsByType: Record<FieldMappingSourceType, string[]>;
	onUpdate: (tempId: string, data: Partial<NewHubspotMapping>) => void;
	onSave: (mapping: NewHubspotMapping) => void;
	onCancel: (tempId: string) => void;
	onConfigureAi: (mapping: NewHubspotMapping) => void;
	isSaving: boolean;
	/** The API's reason when the last save of this row was rejected (PREC-3967). */
	saveError?: string | null;
	ts: Ts;
}

export const HubspotNewMappingRow: FunctionComponent<NewMappingRowProps> = ({
	mapping,
	sourceFields,
	hubspotFields,
	usedSourceFields,
	usedHubspotFieldsByType,
	onUpdate,
	onSave,
	onCancel,
	onConfigureAi,
	isSaving,
	saveError,
	ts,
}) => {
	const isConstant = mapping.sourceType === FieldMappingSourceType.CONSTANT;
	const isAi = mapping.sourceType === FieldMappingSourceType.AI;
	const usedHubspotFields = usedHubspotFieldsByType[mapping.sourceType] ?? [];

	const availableSourceFields = sourceFields.filter(
		(f) => f.name === mapping.sourceField || !usedSourceFields.includes(f.name)
	);
	const availableHubspotFields = hubspotFields.filter(
		(f) =>
			(f.name === mapping.hubspotField || !usedHubspotFields.includes(f.name)) &&
			!isFieldUnwritable(f)
	);

	const sourceOptions: AutoCompleteItem[] = availableSourceFields.map((f) => ({
		id: f.name,
		title: f.label,
	}));

	const hubspotOptions: AutoCompleteItem[] = availableHubspotFields.map((f) => ({
		id: f.name,
		title: `${f.label}${f.isCustom ? " (Custom)" : ""}`,
	}));

	const selectedSource = sourceOptions.find((o) => o.id === mapping.sourceField);
	const selectedHubspot = hubspotOptions.find((o) => o.id === mapping.hubspotField);
	const selectedHubspotField = hubspotFields.find((f) => f.name === mapping.hubspotField);

	const canSave = isAi
		? Boolean(mapping.hubspotField && mapping.aiConfig?.prompt)
		: Boolean(mapping.sourceField) && Boolean(mapping.hubspotField);
	const saveDisabledTooltip = isAi && !mapping.aiConfig?.prompt ? ts("aiSaveDisabledTooltip") : "";
	const badgeLabels = buildBadgeLabels(ts);

	const handleSourceTypeChange = (next: FieldMappingSourceType) => {
		if (next === mapping.sourceType) {
			return;
		}

		onUpdate(mapping.tempId, { sourceType: next, sourceField: "", aiConfig: undefined });
	};

	return (
		<div className="flex flex-col gap-2 py-3 border-b border-gray-100 bg-gray-50 rounded-lg px-2">
			<div className="flex items-start gap-3">
				<div className="flex-1 flex flex-col gap-2">
					<SourceTypePills
						labels={{
							fromRecord: ts("sourceTypeFromRecord"),
							constant: ts("sourceTypeConstant"),
							ai: ts("sourceTypeAi"),
						}}
						testIdPrefix="hubspot"
						value={mapping.sourceType}
						onChange={handleSourceTypeChange}
					/>
					{isAi ? (
						<AiSourceCell
							badgeLabel={ts("aiBadge")}
							configureDisabled={!mapping.hubspotField}
							configureLabel={ts("aiConfigure")}
							creditTooltip={ts("aiCreditTooltip")}
							label={selectedHubspot?.title || ""}
							placeholder={ts("aiTargetPlaceholder")}
							testIdPrefix="hubspot"
							onConfigure={() => onConfigureAi(mapping)}
						/>
					) : isConstant ? (
						<InputField
							maxLength={255}
							name={`hubspot-constant-${mapping.tempId}`}
							placeholder={ts("constantPlaceholder")}
							testId="hubspot-constant-input"
							value={mapping.sourceField}
							handleChange={(e) => onUpdate(mapping.tempId, { sourceField: e.target.value })}
						/>
					) : (
						<AutoComplete
							data={sourceOptions}
							handleReset={() => onUpdate(mapping.tempId, { sourceField: "" })}
							handleSearch={() => {}}
							handleSelect={(item) => {
								if (item) {
									onUpdate(mapping.tempId, { sourceField: item.id as string });
								}
							}}
							placeholder={ts("selectField")}
							selectedItem={selectedSource?.title || ""}
						/>
					)}
				</div>
				<div className="w-[24px] flex items-center justify-center pt-9">
					<Icon className="w-[16px] h-[16px] text-gray-400" icon={faArrowRight} />
				</div>
				<div className="flex-1 pt-9 flex flex-col gap-1">
					<AutoComplete
						data={hubspotOptions}
						handleReset={() =>
							onUpdate(mapping.tempId, {
								hubspotField: "",
								sourceField: isAi ? "" : mapping.sourceField,
							})
						}
						handleSearch={() => {}}
						handleSelect={(item) => {
							if (item) {
								onUpdate(mapping.tempId, {
									hubspotField: item.id as string,
									sourceField: isAi ? item.title : mapping.sourceField,
								});
							}
						}}
						placeholder={ts("selectField")}
						selectedItem={selectedHubspot?.title || ""}
					/>
					{selectedHubspotField && (
						<TargetFieldBadges
							field={selectedHubspotField}
							labels={badgeLabels}
							testIdPrefix="hubspot-new"
						/>
					)}
				</div>
				<div className="w-[60px] flex items-center justify-center pt-9">
					<Toggle
						isChecked={mapping.isActive}
						onChange={() => onUpdate(mapping.tempId, { isActive: !mapping.isActive })}
					/>
				</div>
				<div className="w-[80px] flex gap-2 pt-9">
					<Tooltip content={saveDisabledTooltip} disabled={!saveDisabledTooltip}>
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={!canSave}
								image={
									<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-white" icon={faCheck} />
								}
								isLoading={isSaving}
								size={ButtonSize.XS}
								testId="hubspot-save-new-mapping"
								title=""
								onClick={() => onSave(mapping)}
							/>
						</div>
					</Tooltip>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={
								<Icon className="w-4 h-4 min-w-4 min-h-4 shrink-0 text-gray-700" icon={faXmark} />
							}
							size={ButtonSize.XS}
							title=""
							onClick={() => onCancel(mapping.tempId)}
						/>
					</div>
				</div>
			</div>
			{isAi && (
				<AiHintBanner body={ts("aiHintBody")} testIdPrefix="hubspot" title={ts("aiHintTitle")} />
			)}
			{saveError && (
				<div className="text-xs text-status-error" data-testid="hubspot-mapping-save-error">
					{saveError}
				</div>
			)}
		</div>
	);
};
